import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="document-split"
//
// Loads a multi-page PDF, renders page previews, and lets the user assign a
// document type and truck to each page. Title stages are selected directly
// from the document-type dropdown.
//
// On submit, pages are grouped by truck, document type, and optional title
// stage. One combined PDF is direct-uploaded for each complete group and its
// signed ID is submitted with each page in that group. The untouched source
// packet remains in browser memory and is not uploaded.
//
// PDF libraries (pdfjs-dist ~1.6MB, pdf-lib ~300KB) are loaded lazily on first
// use so they don't bloat the main bundle for every page.
export default class extends Controller {
  static targets = [
    "form",
    "pagesContainer",
    "submitBtn",
    "summary",
    "fallbackPicker",
    "loadingIndicator",
    "pageControlsTemplate",
    "thumbnailControlsTemplate",
    "bulkActions"
  ]

  static values = {
    workerUrl: String,
    titleStageLabels: Object
  }

  // Lazily load PDF libraries on first use. Returns { pdfjsLib, PDFDocument }.
  async loadPdfLibraries() {
    if (!this._pdfLibs) {
      const [pdfjs, pdfLib] = await Promise.all([
        import("pdfjs-dist"),
        import("pdf-lib")
      ])
      pdfjs.GlobalWorkerOptions.workerSrc = this.workerUrlValue
      this._pdfLibs = { pdfjsLib: pdfjs, PDFDocument: pdfLib.PDFDocument }
    }
    return this._pdfLibs
  }

  async connect() {
    if (window.truckDocumentPdfFile) {
      this.file = window.truckDocumentPdfFile
      window.truckDocumentPdfFile = null
      this.pdfBytes = new Uint8Array(await this.file.arrayBuffer())
      this.loadPdf()
    } else {
      this.fallbackPickerTarget.classList.remove("hidden")
    }
  }

  async fallbackFileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    if (file.type !== "application/pdf") {
      this.showToast("error", "Please select a PDF file.")
      return
    }

    const maxSize = 25 * 1024 * 1024
    if (file.size > maxSize) {
      this.showToast("error", "File is too large. Maximum size is 25MB.")
      return
    }

    this.file = file
    this.pdfBytes = new Uint8Array(await file.arrayBuffer())
    this.fallbackPickerTarget.classList.add("hidden")
    this.loadPdf()
  }

  async loadPdf() {
    this.showLoading(true)

    try {
      const { pdfjsLib } = await this.loadPdfLibraries()
      this.pdfDoc = await pdfjsLib.getDocument({ data: this.pdfBytes.slice() }).promise
      await this.renderPages()
    } catch (error) {
      console.error("Failed to load PDF:", error)
      this.showToast("error", "Could not read PDF — the file may be corrupt or password-protected.")
      this.fallbackPickerTarget.classList.remove("hidden")
    } finally {
      this.showLoading(false)
    }
  }

  async renderPages() {
    const totalPages = this.pdfDoc.numPages
    this.pagesContainerTarget.innerHTML = ""

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const page = await this.pdfDoc.getPage(pageNum)

      // Render at high resolution for readability, display at CSS size.
      // Use devicePixelRatio (typically 2x on Retina) with a floor of 2
      // so even on 1x screens the scanned title text is legible.
      const dpr = Math.max(window.devicePixelRatio || 1, 2)
      const renderWidth = 600
      const unscaledViewport = page.getViewport({ scale: 1 })
      const scale = (renderWidth * dpr) / unscaledViewport.width
      const viewport = page.getViewport({ scale })

      // Build collapsible page card. Uses DaisyUI's <details>-based collapse
      // component so we get click-on-summary + keyboard toggling for free.
      // Each card auto-collapses the first time both a document choice and a
      // truck are picked, then stays under the user's manual control.
      const card = document.createElement("details")
      card.className = "collapse collapse-arrow bg-base-100 shadow-sm border border-base-300"
      card.open = true
      card.dataset.pageNum = pageNum
      // Per-card zoom state — each thumbnail tracks its own zoom factor so
      // users can zoom in on the one ambiguous page without affecting the rest.
      card.dataset.thumbnailZoom = "1"

      // Summary row: page number on the left, live assignment summary on
      // the right. The right-side text updates as the user picks a document
      // choice and truck and is what they see when the card is collapsed.
      const summary = document.createElement("summary")
      summary.className = "collapse-title text-sm font-semibold"
      summary.innerHTML = `
        <div class="flex items-baseline justify-between gap-3 pr-6">
          <span>Page ${pageNum} of ${totalPages}</span>
          <span class="text-base-content/60 font-normal" data-card-summary></span>
        </div>
      `

      const cardBody = document.createElement("div")
      cardBody.className = "collapse-content space-y-2"

      // Header: zoom toolbar (page label has moved to the <summary>).
      const header = this.thumbnailControlsTemplateTarget.content.cloneNode(true).firstElementChild

      // Canvas thumbnail wrapped in a full-width, fixed-aspect scroll container.
      // The preview fills the card while its height remains capped below, so
      // portrait pages and zooming past 100% scroll inside the card instead of
      // pushing the assignment controls down the page.
      const scrollWrapper = document.createElement("div")
      scrollWrapper.className = "overflow-auto border border-base-300 rounded"
      scrollWrapper.style.aspectRatio = `${unscaledViewport.width} / ${unscaledViewport.height}`
      scrollWrapper.style.width = "100%"

      const zoomWrapper = document.createElement("div")
      zoomWrapper.dataset.thumbnailZoomWrapper = ""
      zoomWrapper.style.width = "100%"
      zoomWrapper.style.marginInline = "auto"

      const canvas = document.createElement("canvas")
      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.className = "block w-full h-auto select-none"
      canvas.style.aspectRatio = `${unscaledViewport.width} / ${unscaledViewport.height}`

      const ctx = canvas.getContext("2d")
      await page.render({ canvasContext: ctx, viewport }).promise

      zoomWrapper.appendChild(canvas)
      scrollWrapper.appendChild(zoomWrapper)

      // Drag-to-pan inside the card: when zoomed past 100% the canvas
      // overflows its scroll wrapper, and the user can grab it to scroll
      // the wrapper instead of fishing for the scrollbar.
      this._wireDragPan(card, canvas, scrollWrapper)

      // Stamp the shared controls with their per-page Rails form names.
      const controls = this.pageControlsTemplateTarget.content.cloneNode(true)
      controls.querySelector("[data-document-type]").name = `pages[${pageNum}][document_type]`
      controls.querySelector("[data-document-stage]").name = `pages[${pageNum}][stage]`
      controls.querySelector("[data-truck-select]").name = `pages[${pageNum}][deal_id]`

      scrollWrapper.style.maxHeight = "55vh"

      cardBody.appendChild(controls)
      cardBody.appendChild(header)
      cardBody.appendChild(scrollWrapper)
      card.appendChild(summary)
      card.appendChild(cardBody)
      this.pagesContainerTarget.appendChild(card)
    }

    this.bulkActionsTarget.classList.remove("hidden")
    this.updateSummary()
  }

  // ---------------------------------------------------------------------------
  // Per-thumbnail zoom — lightweight CSS-based zoom on each preview card so
  // the +/− buttons are discoverable without opening the full-screen dialog.
  // The canvas is rendered once at high DPR (see renderPages above), so CSS
  // scaling up to ~3x stays sharp without any pdfjs re-render.
  // ---------------------------------------------------------------------------

  thumbnailZoomIn(event) { this._stepThumbnailZoom(event.currentTarget, 1.25) }
  thumbnailZoomOut(event) { this._stepThumbnailZoom(event.currentTarget, 1 / 1.25) }

  thumbnailZoomReset(event) {
    const card = event.currentTarget.closest("[data-page-num]")
    this._setThumbnailZoom(card, 1)
  }

  // The expand-icon button — opens the full-screen viewer for this card's
  // page. Equivalent to clicking the canvas itself but easier to discover.
  thumbnailOpenZoom(event) {
    const card = event.currentTarget.closest("[data-page-num]")
    this.openZoom(Number(card.dataset.pageNum))
  }

  _stepThumbnailZoom(button, factor) {
    const card = button.closest("[data-page-num]")
    const current = parseFloat(card.dataset.thumbnailZoom) || 1
    this._setThumbnailZoom(card, current * factor)
  }

  _setThumbnailZoom(card, zoom) {
    const clamped = Math.max(0.5, Math.min(4, zoom))
    card.dataset.thumbnailZoom = String(clamped)
    const wrapper = card.querySelector("[data-thumbnail-zoom-wrapper]")
    if (wrapper) wrapper.style.width = `${clamped * 100}%`
    const label = card.querySelector("[data-thumbnail-zoom-level]")
    if (label) label.textContent = `${Math.round(clamped * 100)}%`
    // Re-evaluate the grab/default cursor once the new layout has settled.
    if (card.__updateThumbnailCursor) {
      requestAnimationFrame(card.__updateThumbnailCursor)
    }
  }

  // Wire pointer events on a thumbnail canvas so dragging it scrolls the
  // wrapping scroll container. Uses Pointer Events with setPointerCapture so
  // we don't need to attach document-level mousemove/mouseup listeners per
  // card. The cursor reflects state: `grab` when there's something to pan,
  // `grabbing` mid-drag, and `default` when the page already fits.
  _wireDragPan(card, canvas, scrollWrapper) {
    let isPanning = false
    let panStartX, panStartY, scrollStartX, scrollStartY

    const hasOverflow = () =>
      scrollWrapper.scrollWidth > scrollWrapper.clientWidth + 1 ||
      scrollWrapper.scrollHeight > scrollWrapper.clientHeight + 1

    const updateCursor = () => {
      canvas.style.cursor = isPanning
        ? "grabbing"
        : (hasOverflow() ? "grab" : "default")
    }

    canvas.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || !hasOverflow()) return
      canvas.setPointerCapture(event.pointerId)
      isPanning = true
      panStartX = event.clientX
      panStartY = event.clientY
      scrollStartX = scrollWrapper.scrollLeft
      scrollStartY = scrollWrapper.scrollTop
      updateCursor()
      event.preventDefault()
    })

    canvas.addEventListener("pointermove", (event) => {
      if (!isPanning) return
      scrollWrapper.scrollLeft = scrollStartX - (event.clientX - panStartX)
      scrollWrapper.scrollTop = scrollStartY - (event.clientY - panStartY)
    })

    const endPan = (event) => {
      if (!isPanning) return
      isPanning = false
      try { canvas.releasePointerCapture(event.pointerId) } catch (_) { /* ignore */ }
      updateCursor()
    }
    canvas.addEventListener("pointerup", endPan)
    canvas.addEventListener("pointercancel", endPan)

    // Stash the cursor updater on the card so _setThumbnailZoom can re-run
    // it after layout reflows (zoom 100% → 200% should switch to `grab`).
    card.__updateThumbnailCursor = updateCursor
    requestAnimationFrame(updateCursor)
  }

  markAllDocumentType(event) {
    const selectedOption = event.currentTarget.selectedOptions[0]
    const documentType = selectedOption.value
    const stage = selectedOption.dataset.stage || ""

    for (const card of this.pagesContainerTarget.querySelectorAll("[data-page-num]")) {
      const documentTypeSelect = card.querySelector("[data-document-type]")
      const matchingOption = Array.from(documentTypeSelect.options).find(option =>
        option.value === documentType && (option.dataset.stage || "") === stage
      )
      matchingOption.selected = true
      card.querySelector("[data-document-stage]").value = stage
      this._updateCardSummary(card)
      this._maybeAutoCollapse(card)
    }
    this.updateSummary()
  }

  documentTypeChanged(event) {
    const card = event.target.closest("[data-page-num]")
    card.querySelector("[data-document-stage]").value = event.target.selectedOptions[0].dataset.stage || ""
    this._updateCardSummary(card)
    this._maybeAutoCollapse(card)
    this.updateSummary()
  }

  _updateCardSummary(card) {
    const summaryEl = card.querySelector("[data-card-summary]")
    const documentTypeSelect = card.querySelector("[data-document-type]")
    const dealSelect = card.querySelector("[data-truck-select]")
    const parts = []

    if (documentTypeSelect.value) {
      parts.push(documentTypeSelect.selectedOptions[0].textContent.trim())
    }
    if (dealSelect.value) {
      parts.push(dealSelect.selectedOptions[0].textContent.trim())
    }
    summaryEl.textContent = parts.join(" · ")
  }

  _maybeAutoCollapse(card) {
    if (card.dataset.autoCollapsed === "true") return
    const documentType = card.querySelector("[data-document-type]").value
    const dealId = card.querySelector("[data-truck-select]").value
    const stage = card.querySelector("[data-document-stage]").value
    if (!dealId || !documentType || (documentType === "title" && !stage)) return
    card.open = false
    card.dataset.autoCollapsed = "true"
  }

  pageSelections() {
    const fd = new FormData(this.formTarget)
    const totalPages = this.pdfDoc ? this.pdfDoc.numPages : 0
    return Array.from({length: totalPages}, (_, i) => {
      const pageNum = i + 1
      const documentType = fd.get(`pages[${pageNum}][document_type]`) || null
      return {
        pageNum,
        dealId: fd.get(`pages[${pageNum}][deal_id]`) || null,
        documentType,
        stage: documentType === "title" ? (fd.get(`pages[${pageNum}][stage]`) || null) : null
      }
    })
  }

  assignmentChanged(event) {
    const card = event.target.closest("[data-page-num]")
    this._updateCardSummary(card)
    this._maybeAutoCollapse(card)
    this.updateSummary()
  }

  _refreshTruckTitleStageLabels(selections) {
    const pendingStagesByDeal = new Map()
    for (const selection of selections) {
      if (selection.documentType !== "title" || !selection.dealId || !selection.stage) continue

      if (!pendingStagesByDeal.has(selection.dealId)) {
        pendingStagesByDeal.set(selection.dealId, new Set())
      }
      pendingStagesByDeal.get(selection.dealId).add(selection.stage)
    }

    for (const option of this.pagesContainerTarget.querySelectorAll("option[data-persisted-title-stages]")) {
      const baseLabel = option.textContent.split(" — has:")[0]
      const persistedStages = JSON.parse(option.dataset.persistedTitleStages)
      const pendingStages = pendingStagesByDeal.get(option.value) || new Set()
      const stages = Object.keys(this.titleStageLabelsValue).filter(stage =>
        persistedStages.includes(stage) || pendingStages.has(stage)
      )
      const stageLabels = stages.map(stage => this.titleStageLabelsValue[stage])
      option.textContent = stageLabels.length > 0
        ? `${baseLabel} — has: ${stageLabels.join(", ")}`
        : baseLabel
    }

    for (const card of this.pagesContainerTarget.querySelectorAll("[data-page-num]")) {
      this._updateCardSummary(card)
    }
  }

  updateSummary() {
    const totalPages = this.pdfDoc ? this.pdfDoc.numPages : 0
    const selections = this.pageSelections()
    this._refreshTruckTitleStageLabels(selections)
    const complete = selection => selection.dealId && selection.documentType &&
      (selection.documentType !== "title" || selection.stage)
    const assignedCount = selections.filter(complete).length
    const incompleteCount = selections.filter(selection => selection.dealId && !complete(selection)).length

    let summaryText = `${assignedCount}/${totalPages} pages assigned`
    if (incompleteCount > 0) {
      const noun = incompleteCount === 1 ? "page is" : "pages are"
      summaryText += ` — ${incompleteCount} ${noun} missing a document type or title stage`
    }
    this.summaryTarget.textContent = summaryText
    this.submitBtnTarget.disabled = assignedCount === 0 || incompleteCount > 0

  }

  async submit(event) {
    event.preventDefault()
    if (this.submitBtnTarget.disabled) return

    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.innerHTML = '<span class="loading loading-spinner loading-sm"></span> Uploading...'

    try {
      const groups = new Map()
      for (const card of this.pagesContainerTarget.querySelectorAll("[data-page-num]")) {
        const dealId = card.querySelector("[data-truck-select]").value
        const documentType = card.querySelector("[data-document-type]").value
        const stage = documentType === "title"
          ? card.querySelector("[data-document-stage]").value
          : null
        if (!dealId || !documentType || (documentType === "title" && !stage)) continue
        const key = `${dealId}::${documentType}::${stage || ""}`
        if (!groups.has(key)) groups.set(key, [])
        groups.get(key).push(card)
      }

      const { PDFDocument } = await this.loadPdfLibraries()
      for (const cards of groups.values()) {
        const pageNums = cards.map(card => Number(card.dataset.pageNum))
        const documentType = cards[0].querySelector("[data-document-type]").value
        const splitPdf = await PDFDocument.create()
        const srcDoc = await PDFDocument.load(this.pdfBytes)
        const copiedPages = await splitPdf.copyPages(srcDoc, pageNums.map(pageNum => pageNum - 1))
        copiedPages.forEach(page => splitPdf.addPage(page))
        const file = new File(
          [await splitPdf.save()],
          `${documentType}_pages_${pageNums.join("-")}.pdf`,
          { type: "application/pdf" }
        )
        const signedId = await this.directUpload(file)
        for (const card of cards) {
          this.appendSignedIdInput(card, signedId)
        }
      }

      this.submitBtnTarget.innerHTML = '<span class="loading loading-spinner loading-sm"></span> Saving...'
      this.formTarget.requestSubmit()
    } catch (error) {
      console.error("Failed to upload PDF:", error)
      this.showToast("error", "Failed to upload PDF. Please try again.")
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.innerHTML = "Assign Documents"
    }
  }

  appendSignedIdInput(card, signedId) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = `pages[${card.dataset.pageNum}][signed_id]`
    input.value = signedId
    card.appendChild(input)
  }

  async openZoom(pageNum) {
    const page = await this.pdfDoc.getPage(pageNum)
    const zoomController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller~="pdf-zoom"]'),
      "pdf-zoom"
    )
    if (zoomController) {
      zoomController.open(page, pageNum, this.pdfDoc.numPages)
    }
  }

  directUpload(file) {
    const url = "/rails/active_storage/direct_uploads"
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url)
      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob.signed_id)
        }
      })
    })
  }

  showLoading(show) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle("hidden", !show)
    }
  }

  showToast(type, message) {
    document.dispatchEvent(new CustomEvent("toast:show", {
      detail: { type, message }
    }))
  }
}
